import { createAppRequest } from '@/services/request';
import type {
  AdvancementBoardItem,
  AdvancementDetail,
  AdvancementTaskItem,
  AdmissionAssessment,
  ContactRecordItem,
  CustomerDetailAggregate,
  CustomerJudgmentSummary,
  DecisionBudget,
  FollowupScriptItem,
  IndustryTag,
  KeyPerson,
  KnownIssue,
  KnownIssueMutationInput,
  LeadershipFocusItem,
  MaterialItem,
  MaterialSummary,
  MethodologyRecommendationResult,
  OpportunityItem,
  OrgGraphNode,
  OrgGraphNodeType,
  OutcomeReview,
  RiskJudgment,
  SystemItem,
  TagTaxonomy,
  TransformationStageInfo,
} from './types/customer-intelligence';

const request = createAppRequest();
const CI = '/bff/customer-intelligence';
const METH = '/bff/methodology-center/v2';

// ─── Customer Detail ──────────────────────────────────────────────────────────

export async function getCustomerDetail(customerUuid: string): Promise<CustomerDetailAggregate> {
  return request<CustomerDetailAggregate>(`${CI}/customers/${customerUuid}/detail`, {
    method: 'GET',
  });
}

export async function updateCustomerProfile(
  customerUuid: string,
  data: Record<string, unknown>,
): Promise<void> {
  return request<void>(`${CI}/customers/${customerUuid}/profile`, {
    method: 'PUT',
    data,
  });
}

// ─── Org Graph ────────────────────────────────────────────────────────────────

export async function listOrgGraph(customerUuid: string): Promise<OrgGraphNode[]> {
  return request<OrgGraphNode[]>(`${CI}/customers/${customerUuid}/org-graph`, {
    method: 'GET',
  });
}

export async function createOrgNode(
  customerUuid: string,
  data: { name: string; nodeType: OrgGraphNodeType; parentNodeUuid?: string | null; description?: string | null; sortOrder?: number },
): Promise<OrgGraphNode> {
  return request<OrgGraphNode>(`${CI}/customers/${customerUuid}/org-graph`, {
    method: 'POST',
    data,
  });
}

export async function updateOrgNode(
  customerUuid: string,
  nodeUuid: string,
  data: Record<string, unknown>,
): Promise<OrgGraphNode> {
  return request<OrgGraphNode>(`${CI}/customers/${customerUuid}/org-graph/${nodeUuid}`, {
    method: 'PUT',
    data,
  });
}

export async function deleteOrgNode(customerUuid: string, nodeUuid: string): Promise<void> {
  return request<void>(`${CI}/customers/${customerUuid}/org-graph/${nodeUuid}`, {
    method: 'DELETE',
  });
}

export async function reorderOrgNode(
  customerUuid: string,
  data: Record<string, unknown>,
): Promise<void> {
  return request<void>(`${CI}/customers/${customerUuid}/org-graph/reorder`, {
    method: 'POST',
    data,
  });
}

// ─── Key People ───────────────────────────────────────────────────────────────

export async function listKeyPeople(customerUuid: string): Promise<KeyPerson[]> {
  return request<KeyPerson[]>(`${CI}/customers/${customerUuid}/key-people`, {
    method: 'GET',
  });
}

export async function createKeyPerson(
  customerUuid: string,
  data: Record<string, unknown>,
): Promise<KeyPerson> {
  return request<KeyPerson>(`${CI}/customers/${customerUuid}/key-people`, {
    method: 'POST',
    data,
  });
}

export async function updateKeyPerson(
  customerUuid: string,
  personUuid: string,
  data: Record<string, unknown>,
): Promise<KeyPerson> {
  return request<KeyPerson>(
    `${CI}/customers/${customerUuid}/key-people/${personUuid}`,
    { method: 'PUT', data },
  );
}

export async function deleteKeyPerson(
  customerUuid: string,
  personUuid: string,
): Promise<{ success: boolean }> {
  return request<{ success: boolean }>(`${CI}/customers/${customerUuid}/key-people/${personUuid}`, {
    method: 'DELETE',
  });
}

// ─── Tags ──────────────────────────────────────────────────────────────────────

export async function listCustomerTags(customerUuid: string): Promise<TagTaxonomy[]> {
  return request<TagTaxonomy[]>(`${CI}/customers/${customerUuid}/tags`, { method: 'GET' });
}

export async function updateCustomerTagSelection(
  customerUuid: string,
  taxonomyUuid: string,
  tagUuids: string[],
): Promise<TagTaxonomy[]> {
  return request<TagTaxonomy[]>(`${CI}/customers/${customerUuid}/tags/${taxonomyUuid}/selection`, {
    method: 'PUT',
    data: { tagUuids },
  });
}

export async function createCustomTag(
  customerUuid: string,
  data: { taxonomyUuid: string; name: string; [key: string]: unknown },
): Promise<IndustryTag> {
  const { taxonomyUuid, ...rest } = data;
  return request<IndustryTag>(
    `${CI}/customers/${customerUuid}/tags/${taxonomyUuid}/custom`,
    { method: 'POST', data: rest },
  );
}

// ─── Systems ──────────────────────────────────────────────────────────────────

export async function createSystem(
  customerUuid: string,
  data: Record<string, unknown>,
): Promise<SystemItem> {
  return request<SystemItem>(`${CI}/customers/${customerUuid}/systems`, {
    method: 'POST',
    data,
  });
}

export async function updateSystem(
  customerUuid: string,
  systemUuid: string,
  data: Record<string, unknown>,
): Promise<SystemItem> {
  return request<SystemItem>(`${CI}/customers/${customerUuid}/systems/${systemUuid}`, {
    method: 'PUT',
    data,
  });
}

export async function deleteSystem(customerUuid: string, systemUuid: string): Promise<void> {
  return request<void>(`${CI}/customers/${customerUuid}/systems/${systemUuid}`, {
    method: 'DELETE',
  });
}

// ─── Transformation Stage ─────────────────────────────────────────────────────

export async function getTransformationStage(
  customerUuid: string,
): Promise<TransformationStageInfo | null> {
  return request<TransformationStageInfo | null>(
    `${CI}/customers/${customerUuid}/transformation-stage`,
    { method: 'GET' },
  );
}

export async function updateTransformationStage(
  customerUuid: string,
  data: Record<string, unknown>,
): Promise<TransformationStageInfo> {
  return request<TransformationStageInfo>(
    `${CI}/customers/${customerUuid}/transformation-stage`,
    { method: 'PUT', data },
  );
}

// ─── Customer Judgment ───────────────────────────────────────────────────────

export async function getCustomerJudgmentSummary(
  customerUuid: string,
): Promise<CustomerJudgmentSummary> {
  return request<CustomerJudgmentSummary>(`${CI}/customers/${customerUuid}/judgment/summary`, {
    method: 'GET',
  });
}

export async function listAdmissionAssessments(
  customerUuid: string,
): Promise<AdmissionAssessment[]> {
  return request<AdmissionAssessment[]>(
    `${CI}/customers/${customerUuid}/admission-assessments`,
    { method: 'GET' },
  );
}

export async function createAdmissionAssessment(
  customerUuid: string,
  data: Record<string, unknown>,
): Promise<AdmissionAssessment> {
  return request<AdmissionAssessment>(`${CI}/customers/${customerUuid}/admission-assessments`, {
    method: 'POST',
    data,
  });
}

export async function updateAdmissionAssessment(
  customerUuid: string,
  assessmentUuid: string,
  data: Record<string, unknown>,
): Promise<AdmissionAssessment> {
  return request<AdmissionAssessment>(
    `${CI}/customers/${customerUuid}/admission-assessments/${assessmentUuid}`,
    { method: 'PUT', data },
  );
}

export async function deleteAdmissionAssessment(
  customerUuid: string,
  assessmentUuid: string,
): Promise<void> {
  return request<void>(`${CI}/customers/${customerUuid}/admission-assessments/${assessmentUuid}`, {
    method: 'DELETE',
  });
}

export async function generateAdmissionAssessment(
  customerUuid: string,
): Promise<AdmissionAssessment> {
  return request<AdmissionAssessment>(
    `${CI}/customers/${customerUuid}/admission-assessments/generate`,
    { method: 'POST' },
  );
}

export async function generateTransformationStage(
  customerUuid: string,
): Promise<TransformationStageInfo> {
  return request<TransformationStageInfo>(
    `${CI}/customers/${customerUuid}/transformation-stage/generate`,
    { method: 'POST' },
  );
}

export async function getDecisionBudget(customerUuid: string): Promise<DecisionBudget | null> {
  return request<DecisionBudget | null>(`${CI}/customers/${customerUuid}/decision-budget`, {
    method: 'GET',
  });
}

export async function updateDecisionBudget(
  customerUuid: string,
  data: Record<string, unknown>,
): Promise<DecisionBudget> {
  return request<DecisionBudget>(`${CI}/customers/${customerUuid}/decision-budget`, {
    method: 'PUT',
    data,
  });
}

export async function generateDecisionBudget(customerUuid: string): Promise<DecisionBudget> {
  return request<DecisionBudget>(`${CI}/customers/${customerUuid}/decision-budget/generate`, {
    method: 'POST',
  });
}

export async function generateLeadershipFocuses(
  customerUuid: string,
): Promise<{ items: LeadershipFocusItem[]; total: number }> {
  return request<{ items: LeadershipFocusItem[]; total: number }>(
    `${CI}/customers/${customerUuid}/leadership-focuses/generate`,
    { method: 'POST' },
  );
}

export async function listRiskJudgments(customerUuid: string): Promise<RiskJudgment[]> {
  return request<RiskJudgment[]>(`${CI}/customers/${customerUuid}/risk-judgments`, {
    method: 'GET',
  });
}

export async function createRiskJudgment(
  customerUuid: string,
  data: Record<string, unknown>,
): Promise<RiskJudgment> {
  return request<RiskJudgment>(`${CI}/customers/${customerUuid}/risk-judgments`, {
    method: 'POST',
    data,
  });
}

export async function updateRiskJudgment(
  customerUuid: string,
  riskUuid: string,
  data: Record<string, unknown>,
): Promise<RiskJudgment> {
  return request<RiskJudgment>(`${CI}/customers/${customerUuid}/risk-judgments/${riskUuid}`, {
    method: 'PUT',
    data,
  });
}

export async function deleteRiskJudgment(customerUuid: string, riskUuid: string): Promise<void> {
  return request<void>(`${CI}/customers/${customerUuid}/risk-judgments/${riskUuid}`, {
    method: 'DELETE',
  });
}

export async function generateRiskJudgments(
  customerUuid: string,
): Promise<{ items: RiskJudgment[]; total: number }> {
  return request<{ items: RiskJudgment[]; total: number }>(
    `${CI}/customers/${customerUuid}/risk-judgments/generate`,
    { method: 'POST' },
  );
}

// ─── Materials ────────────────────────────────────────────────────────────────

export async function createMaterial(
  customerUuid: string,
  data: Record<string, unknown>,
): Promise<MaterialItem> {
  return request<MaterialItem>(`${CI}/customers/${customerUuid}/materials`, {
    method: 'POST',
    data,
  });
}

export async function updateMaterial(
  customerUuid: string,
  materialUuid: string,
  data: Record<string, unknown>,
): Promise<MaterialItem> {
  return request<MaterialItem>(`${CI}/customers/${customerUuid}/materials/${materialUuid}`, {
    method: 'PUT',
    data,
  });
}

export async function deleteMaterial(customerUuid: string, materialUuid: string): Promise<void> {
  return request<void>(`${CI}/customers/${customerUuid}/materials/${materialUuid}`, {
    method: 'DELETE',
  });
}

export async function triggerMaterialSummary(
  customerUuid: string,
  materialUuid: string,
): Promise<MaterialSummary> {
  return request<MaterialSummary>(
    `${CI}/customers/${customerUuid}/materials/${materialUuid}/summarize`,
    { method: 'POST' },
  );
}

// ─── Leadership Focuses ───────────────────────────────────────────────────────

export async function listLeadershipFocuses(
  customerUuid: string,
): Promise<LeadershipFocusItem[]> {
  return request<LeadershipFocusItem[]>(`${CI}/customers/${customerUuid}/leadership-focuses`, {
    method: 'GET',
  });
}

export async function createLeadershipFocus(
  customerUuid: string,
  data: Record<string, unknown>,
): Promise<LeadershipFocusItem> {
  return request<LeadershipFocusItem>(`${CI}/customers/${customerUuid}/leadership-focuses`, {
    method: 'POST',
    data,
  });
}

export async function updateLeadershipFocus(
  customerUuid: string,
  focusUuid: string,
  data: Record<string, unknown>,
): Promise<LeadershipFocusItem> {
  return request<LeadershipFocusItem>(
    `${CI}/customers/${customerUuid}/leadership-focuses/${focusUuid}`,
    { method: 'PUT', data },
  );
}

export async function deleteLeadershipFocus(
  customerUuid: string,
  focusUuid: string,
): Promise<void> {
  return request<void>(
    `${CI}/customers/${customerUuid}/leadership-focuses/${focusUuid}`,
    { method: 'DELETE' },
  );
}

// ─── Contacts ─────────────────────────────────────────────────────────────────

export async function createContact(
  customerUuid: string,
  data: Record<string, unknown>,
): Promise<ContactRecordItem> {
  return request<ContactRecordItem>(`${CI}/customers/${customerUuid}/contacts`, {
    method: 'POST',
    data,
  });
}

export async function updateContact(
  customerUuid: string,
  contactUuid: string,
  data: Record<string, unknown>,
): Promise<ContactRecordItem> {
  return request<ContactRecordItem>(
    `${CI}/customers/${customerUuid}/contacts/${contactUuid}`,
    { method: 'PUT', data },
  );
}

export async function deleteContact(customerUuid: string, contactUuid: string): Promise<void> {
  return request<void>(`${CI}/customers/${customerUuid}/contacts/${contactUuid}`, {
    method: 'DELETE',
  });
}

export async function importContacts(
  customerUuid: string,
  data: unknown[],
): Promise<{ items: ContactRecordItem[] }> {
  return request<{ items: ContactRecordItem[] }>(`${CI}/customers/${customerUuid}/contacts/import`, {
    method: 'POST',
    data,
  });
}

// ─── Known Issues ─────────────────────────────────────────────────────────────

export async function createKnownIssue(
  customerUuid: string,
  data: KnownIssueMutationInput,
): Promise<KnownIssue> {
  return request<KnownIssue>(`${CI}/customers/${customerUuid}/known-issues`, {
    method: 'POST',
    data,
  });
}

export async function updateKnownIssue(
  customerUuid: string,
  issueUuid: string,
  data: KnownIssueMutationInput,
): Promise<KnownIssue> {
  return request<KnownIssue>(`${CI}/customers/${customerUuid}/known-issues/${issueUuid}`, {
    method: 'PUT',
    data,
  });
}

export async function deleteKnownIssue(customerUuid: string, issueUuid: string): Promise<void> {
  return request<void>(`${CI}/customers/${customerUuid}/known-issues/${issueUuid}`, {
    method: 'DELETE',
  });
}

// ─── Opportunities ────────────────────────────────────────────────────────────

export async function createOpportunity(
  customerUuid: string,
  data: Record<string, unknown>,
): Promise<OpportunityItem> {
  return request<OpportunityItem>(`${CI}/customers/${customerUuid}/opportunities`, {
    method: 'POST',
    data,
  });
}

export async function updateOpportunity(
  customerUuid: string,
  opportunityUuid: string,
  data: Record<string, unknown>,
): Promise<OpportunityItem> {
  return request<OpportunityItem>(
    `${CI}/customers/${customerUuid}/opportunities/${opportunityUuid}`,
    { method: 'PUT', data },
  );
}

export async function deleteOpportunity(
  customerUuid: string,
  opportunityUuid: string,
): Promise<void> {
  return request<void>(
    `${CI}/customers/${customerUuid}/opportunities/${opportunityUuid}`,
    { method: 'DELETE' },
  );
}

// ─── Advancement ──────────────────────────────────────────────────────────────

export async function getAdvancementDetail(
  customerUuid: string,
  opportunityUuid: string,
): Promise<AdvancementDetail> {
  return request<AdvancementDetail>(
    `${CI}/customers/${customerUuid}/opportunities/${opportunityUuid}/advancement/detail`,
    { method: 'GET' },
  );
}

export async function upsertAdvancement(
  customerUuid: string,
  opportunityUuid: string,
  data: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  return request<Record<string, unknown>>(
    `${CI}/customers/${customerUuid}/opportunities/${opportunityUuid}/advancement`,
    { method: 'PUT', data },
  );
}

export async function createAdvancementTask(
  customerUuid: string,
  opportunityUuid: string,
  data: Record<string, unknown>,
): Promise<AdvancementTaskItem> {
  return request<AdvancementTaskItem>(
    `${CI}/customers/${customerUuid}/opportunities/${opportunityUuid}/advancement/tasks`,
    { method: 'POST', data },
  );
}

export async function updateAdvancementTask(
  customerUuid: string,
  opportunityUuid: string,
  taskUuid: string,
  data: Record<string, unknown>,
): Promise<AdvancementTaskItem> {
  return request<AdvancementTaskItem>(
    `${CI}/customers/${customerUuid}/opportunities/${opportunityUuid}/advancement/tasks/${taskUuid}`,
    { method: 'PUT', data },
  );
}

export async function deleteAdvancementTask(
  customerUuid: string,
  opportunityUuid: string,
  taskUuid: string,
): Promise<void> {
  return request<void>(
    `${CI}/customers/${customerUuid}/opportunities/${opportunityUuid}/advancement/tasks/${taskUuid}`,
    { method: 'DELETE' },
  );
}

export async function listOverdueAdvancementTasks(): Promise<{
  items: AdvancementTaskItem[];
  total: number;
}> {
  return request<{ items: AdvancementTaskItem[]; total: number }>(
    `${CI}/advancement/tasks/overdue`,
    { method: 'GET' },
  );
}

export async function listAdvancementBoard(params?: Record<string, unknown>): Promise<{
  items: AdvancementBoardItem[];
  total: number;
}> {
  return request<{ items: AdvancementBoardItem[]; total: number }>(`${CI}/advancement/board`, {
    method: 'GET',
    params,
  });
}

export async function generateAdvancementDoc(
  customerUuid: string,
  opportunityUuid: string,
  data: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  return request<Record<string, unknown>>(
    `${CI}/customers/${customerUuid}/opportunities/${opportunityUuid}/advancement/docs/generate`,
    { method: 'POST', data },
  );
}

export async function generateFollowupScript(
  customerUuid: string,
  opportunityUuid: string,
  data: Record<string, unknown>,
): Promise<FollowupScriptItem> {
  return request<FollowupScriptItem>(
    `${CI}/customers/${customerUuid}/opportunities/${opportunityUuid}/advancement/scripts/generate`,
    { method: 'POST', data },
  );
}

// ─── Outcome Review ───────────────────────────────────────────────────────────

export async function generateOutcomeReview(data: Record<string, unknown>): Promise<OutcomeReview> {
  return request<OutcomeReview>(`${METH}/outcome-review/generate`, { method: 'POST', data });
}

export async function confirmOutcomeReview(
  reviewUuid: string,
  data: Record<string, unknown>,
): Promise<OutcomeReview> {
  return request<OutcomeReview>(`${METH}/outcome-review/${reviewUuid}/confirm`, {
    method: 'POST',
    data,
  });
}

// ─── Recommendation ───────────────────────────────────────────────────────────

export async function recommendSimilarCases(
  data: Record<string, unknown>,
): Promise<MethodologyRecommendationResult> {
  return request<MethodologyRecommendationResult>(`${METH}/recommend/similar-cases`, {
    method: 'POST',
    data,
  });
}

export async function recommendBestPlaybook(
  data: Record<string, unknown>,
): Promise<MethodologyRecommendationResult> {
  return request<MethodologyRecommendationResult>(`${METH}/recommend/best-playbook`, {
    method: 'POST',
    data,
  });
}

export async function recommendNextAction(
  data: Record<string, unknown>,
): Promise<MethodologyRecommendationResult> {
  return request<MethodologyRecommendationResult>(`${METH}/recommend/next-action`, {
    method: 'POST',
    data,
  });
}

export async function submitRecommendationFeedback(
  logUuid: string | null | undefined,
  data: Record<string, unknown>,
): Promise<void> {
  if (!logUuid) return;
  return request<void>(`${METH}/recommend/logs/${logUuid}/feedback`, {
    method: 'POST',
    data,
  });
}
